import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { DocumentFile } from '@shared/types'
import { api, del, get, isOffline, patch } from '../lib/api'
import { formatDate } from '../lib/format'
import { formatBytes } from '../lib/image'
import { useToday } from '../state/today'
import { useToast } from '../state/toast'
import { SiteSelect } from '../components/AddSiteSheet'
import { Button, EmptyState, ErrorNote, Field, Sheet, Spinner } from '../components/ui'

/** What the file inputs offer; the server holds the same list. */
const ACCEPT = '.pdf,.jpg,.jpeg,.png,.webp,.heic,.doc,.docx,.xls,.xlsx'

function kindLabel(doc: DocumentFile): string {
  if (doc.contentType === 'application/pdf') return 'PDF'
  if (doc.contentType.startsWith('image/')) return 'Photo'
  if (doc.contentType.includes('word')) return 'Word'
  if (doc.contentType.includes('sheet') || doc.contentType.includes('excel')) return 'Excel'
  return 'File'
}

/**
 * Drawings, permits, spec sheets — anything the crew needs on site. Anyone
 * can add one; the office can rename, re-file, replace or delete; whoever
 * added a document can delete it.
 */
export default function DocumentsPage() {
  const { state } = useToday()
  const toast = useToast()
  const [params, setParams] = useSearchParams()
  const [docs, setDocs] = useState<DocumentFile[] | null>(null)
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<DocumentFile | null>(null)
  const siteId = params.get('siteId') ?? ''

  const load = useCallback(async () => {
    const res = await get<{ documents: DocumentFile[] }>(
      `/api/documents${siteId ? `?siteId=${encodeURIComponent(siteId)}` : ''}`,
    )
    setDocs(res.documents)
  }, [siteId])

  useEffect(() => {
    setDocs(null)
    void load().catch(() => setDocs([]))
  }, [load])

  if (!state) return <div className="py-16"><Spinner /></div>

  const isAdmin = state.user.role === 'admin'
  const done = async (message: string) => {
    setAdding(false)
    setEditing(null)
    toast.success(message)
    await load().catch(() => undefined)
  }

  return (
    <div className="space-y-5">
      <header className="flex items-end justify-between gap-4">
        <div>
          <p className="label">Drawings, permits, specs</p>
          <h1 className="mt-0.5 text-2xl">Documents</h1>
        </div>
        <Button variant="primary" className="px-3" onClick={() => setAdding(true)}>
          Add
        </Button>
      </header>

      <select
        className="field"
        value={siteId}
        aria-label="Filter by site"
        onChange={(e) => {
          const next = new URLSearchParams(params)
          if (e.target.value) next.set('siteId', e.target.value)
          else next.delete('siteId')
          setParams(next, { replace: true })
        }}
      >
        <option value="">All sites</option>
        <option value="general">General (not one site)</option>
        {state.sites.map((site) => <option key={site.id} value={site.id}>{site.name}</option>)}
      </select>

      {!docs ? (
        <div className="py-10"><Spinner /></div>
      ) : docs.length === 0 ? (
        <EmptyState
          title="No documents"
          body={siteId ? 'Nothing has been added for this yet.' : 'Add the drawings and specs the crew needs on site.'}
          action={<Button onClick={() => setAdding(true)}>Add a document</Button>}
        />
      ) : (
        <div className="space-y-2">
          {docs.map((doc) => {
            const canDelete = isAdmin || doc.uploadedBy === state.user.id
            return (
              <div key={doc.id} className="card flex items-center gap-3 p-3">
                <a
                  href={doc.url}
                  target="_blank"
                  rel="noopener"
                  className="flex min-w-0 flex-1 items-center gap-3"
                >
                  <span className="chip shrink-0 bg-ink text-brand">{kindLabel(doc)}</span>
                  <span className="min-w-0">
                    <span className="block truncate font-bold">{doc.title}</span>
                    <span className="block truncate text-xs text-graphite-2">
                      {doc.siteName ?? 'General'} · {doc.uploadedByName} · {formatDate(doc.createdAt, state.timezone)}
                      {' · '}{formatBytes(doc.sizeBytes)}
                    </span>
                  </span>
                </a>
                {canDelete && (
                  <Button className="min-h-10 shrink-0 px-3 text-xs" onClick={() => setEditing(doc)}>
                    {isAdmin ? 'Edit' : 'Manage'}
                  </Button>
                )}
              </div>
            )
          })}
        </div>
      )}

      {adding && (
        <AddDocumentSheet
          defaultSiteId={siteId && siteId !== 'general' ? siteId : state.openEntry?.siteId ?? ''}
          onClose={() => setAdding(false)}
          onDone={done}
        />
      )}
      {editing && (
        <EditDocumentSheet
          doc={editing}
          isAdmin={isAdmin}
          onClose={() => setEditing(null)}
          onDone={done}
        />
      )}
    </div>
  )
}

function AddDocumentSheet({ defaultSiteId, onClose, onDone }: {
  defaultSiteId: string
  onClose: () => void
  onDone: (message: string) => Promise<void>
}) {
  const { state } = useToday()
  const fileRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [title, setTitle] = useState('')
  const [siteId, setSiteId] = useState(defaultSiteId)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const upload = async () => {
    if (!file) return setError('Choose a file first.')
    if (file.size > 25 * 1024 * 1024) return setError('That file is over 25 MB.')
    setBusy(true)
    setError(null)
    const form = new FormData()
    form.set('file', file)
    form.set('title', title.trim() || file.name.replace(/\.[^.]+$/, ''))
    if (siteId) form.set('siteId', siteId)
    try {
      await api('/api/documents', { method: 'POST', body: form })
      await onDone('Document added')
    } catch (err) {
      setError(isOffline(err)
        ? 'Documents need signal to upload. Try again when you have some.'
        : err instanceof Error ? err.message : 'Could not upload')
      setBusy(false)
    }
  }

  return (
    <Sheet
      open
      onClose={onClose}
      title="Add a document"
      footer={
        <Button variant="primary" size="lg" className="w-full" loading={busy} disabled={!file} onClick={() => void upload()}>
          {file ? 'Upload' : 'Choose a file first'}
        </Button>
      }
    >
      <div className="space-y-4">
        <div>
          <input
            ref={fileRef}
            type="file"
            accept={ACCEPT}
            className="hidden"
            onChange={(e) => {
              const picked = e.target.files?.[0] ?? null
              setFile(picked)
              if (picked && !title) setTitle(picked.name.replace(/\.[^.]+$/, ''))
            }}
          />
          <Button className="w-full" onClick={() => fileRef.current?.click()}>
            {file ? 'Choose a different file' : 'Choose a file'}
          </Button>
          <p className="mt-1 text-xs text-graphite-2">
            {file
              ? `${file.name} · ${formatBytes(file.size)}`
              : 'PDF, photo, Word or Excel, up to 25 MB.'}
          </p>
        </div>
        <Field label="Title">
          <input
            className="field"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Second floor framing plan"
          />
        </Field>
        <Field label="Site">
          <SiteSelect
            value={siteId}
            onChange={setSiteId}
            sites={state?.sites ?? []}
            emptyLabel="General — not one site"
          />
        </Field>
        <ErrorNote>{error}</ErrorNote>
      </div>
    </Sheet>
  )
}

function EditDocumentSheet({ doc, isAdmin, onClose, onDone }: {
  doc: DocumentFile
  isAdmin: boolean
  onClose: () => void
  onDone: (message: string) => Promise<void>
}) {
  const { state } = useToday()
  const fileRef = useRef<HTMLInputElement>(null)
  const [title, setTitle] = useState(doc.title)
  const [siteId, setSiteId] = useState(doc.siteId ?? '')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const run = async (action: string, fn: () => Promise<unknown>, message: string) => {
    setBusy(action)
    setError(null)
    try {
      await fn()
      await onDone(message)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not go through')
      setBusy(null)
    }
  }

  const replace = (file: File) => {
    const form = new FormData()
    form.set('file', file)
    void run('replace', () => api(`/api/documents/${doc.id}/file`, { method: 'PUT', body: form }), 'File replaced')
  }

  return (
    <Sheet
      open
      onClose={onClose}
      title={doc.title}
      footer={isAdmin ? (
        <Button
          variant="primary"
          size="lg"
          className="w-full"
          loading={busy === 'save'}
          onClick={() => void run('save', () => patch(`/api/documents/${doc.id}`, {
            title: title.trim(), siteId: siteId || null,
          }), 'Document saved')}
        >
          Save
        </Button>
      ) : undefined}
    >
      <div className="space-y-4">
        {isAdmin && (
          <>
            <Field label="Title" required>
              <input className="field" value={title} onChange={(e) => setTitle(e.target.value)} />
            </Field>
            <Field label="Site">
              <SiteSelect
                value={siteId}
                onChange={setSiteId}
                sites={state?.sites ?? []}
                emptyLabel="General — not one site"
              />
            </Field>
            <input
              ref={fileRef}
              type="file"
              accept={ACCEPT}
              className="hidden"
              onChange={(e) => {
                const picked = e.target.files?.[0]
                if (picked) replace(picked)
              }}
            />
            <Button className="w-full" loading={busy === 'replace'} onClick={() => fileRef.current?.click()}>
              Replace the file
            </Button>
          </>
        )}

        <ErrorNote>{error}</ErrorNote>

        <div className={isAdmin ? 'border-t border-line pt-4' : ''}>
          {confirmDelete ? (
            <div className="space-y-2">
              <p className="text-sm">Delete {doc.title}? Nobody will be able to open it again.</p>
              <div className="flex gap-2">
                <Button className="flex-1" onClick={() => setConfirmDelete(false)}>Keep it</Button>
                <Button
                  variant="danger"
                  className="flex-1"
                  loading={busy === 'delete'}
                  onClick={() => void run('delete', () => del(`/api/documents/${doc.id}`), 'Document deleted')}
                >
                  Delete
                </Button>
              </div>
            </div>
          ) : (
            <Button variant="ghost" className="w-full text-alarm" onClick={() => setConfirmDelete(true)}>
              Delete this document
            </Button>
          )}
        </div>
      </div>
    </Sheet>
  )
}
